import Pagination from 'antd/es/pagination';
import Table, { type ColumnType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { CardView, StyledTable, TableView } from './DataTable/DataTable.styles';
import type {
  DataTableColumnType,
  DataTableProps,
  FilterConfig,
} from './DataTable/DataTable.types';
import EmptyState from './EmptyState';
import { FilterBar } from './FilterBar';
import { ScrollableTable } from './ScrollableTable';
import { TableCard } from './TableCard';

const StyledPagination = styled(Pagination)`
  ${({ theme }) => css`
    display: none;

    @container (max-width: ${theme.screenMD}px) {
      display: block;
      margin-bottom: ${theme.marginSM}px;
    }
  `}
`;

export function DataTable<
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  columns,
  data,
  loading = false,
  emptyState,
  maxVisibleFilters = 4,
  cardFields = [],
  mobileBreakpoint: _mobileBreakpoint,
  scrollable = false,
  maxHeight = 600,
  size = 'small',
  ...tableProps
}: DataTableProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filterConfigs = useMemo<FilterConfig[]>(() => {
    const configs: FilterConfig[] = [];
    columns.forEach((col: DataTableColumnType<T>) => {
      if (!col.filter || !col.key) {
        return;
      }
      const key = col.key.toString();

      const handleChange = (value: string | null) => {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
        // Reset page to 1 when filters change
        newParams.set('page', '1');
        setSearchParams(newParams);
      };

      const commonProps = {
        key,
        label: col.title as string,
        placeholder: col.filter.placeholder,
        width: col.filter.width,
        onChange: handleChange,
      };

      switch (col.filter.type) {
        case 'search':
          configs.push({
            ...commonProps,
            type: 'search',
            value: searchParams.get(key) || '',
          });
          break;
        case 'select':
          configs.push({
            ...commonProps,
            type: 'select',
            options: col.filter.options,
            value: searchParams.get(key) || undefined,
          });
          break;
        case 'dateRange':
          // TODO: Implement date range filter
          break;
        case 'number':
          // TODO: Implement number filter
          break;
        case 'boolean':
          // TODO: Implement boolean filter
          break;
      }
    });
    return configs;
  }, [columns, searchParams, setSearchParams]);

  const finalEmptyState = useMemo(() => {
    if (!emptyState) {
      return undefined;
    }

    const hasActiveFilters = filterConfigs.some((f) => {
      if (!f.key) return false;
      return searchParams.has(f.key);
    });

    if (hasActiveFilters && emptyState.overrideOnFiltersPresent) {
      return {
        ...emptyState,
        ...emptyState.overrideOnFiltersPresent,
      };
    }

    return emptyState;
  }, [emptyState, filterConfigs, searchParams]);

  // Find action column for card view
  const actionColumn = columns.find((col) => col.key === 'actions');

  // Auto-generate card fields from columns if not provided
  const finalCardFields =
    cardFields.length > 0
      ? cardFields
      : columns
          .filter((col) => col.key !== 'actions')
          .slice(0, 5) // Limit to first 5 columns
          .map((col, index) => ({
            key: ('dataIndex' in col ? col.dataIndex : col.key) as keyof T,
            label: typeof col.title === 'string' ? col.title : String(col.key),
            priority: (index === 0 ? 'high' : index < 3 ? 'medium' : 'low') as
              | 'high'
              | 'medium'
              | 'low',
            render:
              'render' in col && col.render
                ? (value: unknown, record: T) =>
                    col.render!(value, record, 0) as React.ReactNode
                : undefined,
          }));

  const hasFilters = filterConfigs.length > 0;

  const tableColumns = useMemo(() => {
    const orderBy = searchParams.get('orderBy');
    const orderDirection = searchParams.get('orderDirection');

    return columns.map((col): ColumnType<T> => {
      const { filter: _filter, ...restCol } = col;
      if (!restCol.sorter) {
        return restCol;
      }
      const sortOrder: SortOrder | undefined =
        restCol.key === orderBy
          ? orderDirection === 'desc'
            ? 'descend'
            : 'ascend'
          : undefined;
      return {
        ...restCol,
        sortOrder,
      };
    });
  }, [columns, searchParams]);

  const tableComponent = (
    <StyledTable size={size}>
      <Table<T>
        columns={tableColumns}
        dataSource={data}
        loading={loading}
        size={size}
        rowKey={
          typeof tableProps.rowKey === 'function'
            ? (record: T) =>
                String(
                  (tableProps.rowKey as (record: T) => string | number)(record),
                )
            : String(tableProps.rowKey || 'key')
        }
        locale={{
          emptyText: finalEmptyState ? (
            <EmptyState
              icon={finalEmptyState.icon}
              title={finalEmptyState.title}
              description={finalEmptyState.description}
              action={finalEmptyState.action}
            />
          ) : undefined,
          ...tableProps.locale,
        }}
        onChange={(pagination, _filters, sorter) => {
          const newParams = new URLSearchParams(searchParams);

          // Handle pagination.
          // The pagination object passed to onChange has the new page number and page size.
          if (pagination.current) {
            newParams.set('page', pagination.current.toString());
          }
          if (pagination.pageSize) {
            newParams.set('pageSize', pagination.pageSize.toString());
          }

          // Handle sorting
          const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
          if (
            activeSorter &&
            activeSorter.field &&
            typeof activeSorter.field === 'string' &&
            activeSorter.order
          ) {
            newParams.set('orderBy', activeSorter.field);
            newParams.set(
              'orderDirection',
              activeSorter.order === 'descend' ? 'desc' : 'asc',
            );
          } else {
            // If the sorter is cleared, remove the sort parameters from the URL.
            // This happens when the user cycles through ascend, descend, and no-sort states.
            newParams.delete('orderBy');
            newParams.delete('orderDirection');
          }

          setSearchParams(newParams);
        }}
        pagination={
          tableProps.pagination === false
            ? false
            : data.length > 0
              ? {
                  current: Number(searchParams.get('page')) || 1,
                  pageSize:
                    Number(searchParams.get('pageSize')) ||
                    (typeof tableProps.pagination === 'object' &&
                      tableProps.pagination?.pageSize) ||
                    20,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                  ...((typeof tableProps.pagination === 'object' &&
                    tableProps.pagination) ||
                    {}),
                }
              : false
        }
      />
    </StyledTable>
  );

  const handleClearFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    filterConfigs.forEach((filter) => {
      newParams.delete(filter.key);
    });
    setSearchParams(newParams);
  };

  return (
    <div>
      {/* Filter Bar */}
      {hasFilters && (
        <FilterBar
          filters={filterConfigs}
          onClearAll={handleClearFilters}
          maxVisible={maxVisibleFilters}
        />
      )}
      <StyledPagination
        showQuickJumper
        showSizeChanger
        current={Number(searchParams.get('page')) || 1}
        pageSize={Number(searchParams.get('pageSize')) || 10}
        total={
          typeof tableProps.pagination === 'object'
            ? (tableProps.pagination.total ?? 1)
            : 1
        }
        onChange={(page, pageSize) => {
          setSearchParams((prev) => {
            prev.set('page', page.toString());
            prev.set('pageSize', pageSize.toString());
            return prev;
          });
        }}
      />

      {/* Desktop Table View */}
      <TableView>
        {scrollable ? (
          <ScrollableTable maxHeight={maxHeight} scrollable={scrollable}>
            {tableComponent}
          </ScrollableTable>
        ) : (
          tableComponent
        )}
      </TableView>

      {/* Mobile Card View */}
      <CardView>
        <TableCard
          data={data}
          cardFields={finalCardFields}
          loading={loading}
          emptyState={finalEmptyState}
          rowKey={
            typeof tableProps.rowKey === 'function'
              ? (record: T) =>
                  String(
                    (tableProps.rowKey as (record: T) => string | number)(
                      record,
                    ),
                  )
              : String(tableProps.rowKey || 'key')
          }
          actionColumn={actionColumn}
        />
      </CardView>
    </div>
  );
}

// Export the EmptyStateConfig interface for backward compatibility
export type { EmptyStateConfig } from './DataTable/DataTable.types';
